/* eslint-disable @typescript-eslint/no-explicit-any */
import { openDB, IDBPTransaction, DBSchema, IDBPObjectStore, IDBPDatabase } from 'idb';
import { BaseEntity, BaseEvent, BaseProjectMetadata } from './structures';
import { EntityCollection } from './EntityCollection';

// Fields used for indexes and keys
const FIELD_CX = 'cx';
const FIELD_BX = 'bx';
const FIELD_PI = 'pi';
const FIELD_ID = 'id';
const FIELD_MODIFIED_ON = 'modifiedOn';

// Used index names
const STOREINDEX_PI_CX = 'index_pi_cx';
const STOREINDEX_PI_BX_CX = 'index_pi_bx_cx';
const STOREINDEX_PI = 'index_projectId';
const STOREINDEX_MODIFIED_ON = 'index_modifiedOn';

export interface EventSourceStoreConfig {
    appName: string;
    dbVersion: number;
    storeNames: {
        events: string;
        aggregates: string;
        entities: Record<string, string>;
    };
    onUpgrade?: (
        db: IDBPDatabase<unknown>,
        oldVersion: number,
        newVersion: number | null,
        transaction: IDBPTransaction<unknown, string[], "versionchange">
    ) => void;
}

export interface IEventSourceStore<
    TEventType extends number,
    TEvent extends BaseEvent<TEventType>,
    TAggregate extends BaseProjectMetadata,
    TEntityType extends BaseEntity<TEventType>,
    TEntityTypes extends Record<string, TEntityType>
> {
    /**
    * Saves aggregate metadata, events, and multiple entity collections in a single transaction
    * @param aggregate The aggregate metadata to save
    * @param events Array of events to save
    * @param entitiesToSave Map of entity type keys to sets of entities to save
    */
    saveWorld(
        aggregate: TAggregate,
        events: TEvent[],
        entitiesToSave: Map<keyof TEntityTypes, Set<TEntityType>>
    ): Promise<void>
    getAggregateById(aggregateId: string): Promise<TAggregate | undefined>;
    writeAll: (operations: (worker: IStoreWriter<TEventType, TEvent, TAggregate, TEntityType, TEntityTypes>) => Promise<void>) => Promise<void>;
    getEvents: <T extends TEvent>(aggregateId: string, fromClientIndex?: number, toClientIndex?: number) => Promise<T[]>;
    getServerEvents<T extends TEvent>(aggregateId: string, fromBatchIndex?: number, toSi?: number, fromClientIndex?: number, toClientIndex?: number): Promise<T[]>;
    isAggregateExist(aggregateId: string): Promise<boolean>;
    getAllAggregates: () => Promise<TAggregate[]>;
    deleteAggregate(aggregateId: string): Promise<void>;
    getMostRecentAggregate(): Promise<TAggregate | undefined>;
    getEntitiesByAggregateId: <K extends keyof TEntityTypes>(entityType: K, aggregateId: string) => Promise<TEntityTypes[K][]>;
    replaceWorld(
        newMetadata: TAggregate,
        oldEntities: Record<string, EntityCollection<TEntityType>>,
        newEntities: Record<string, EntityCollection<TEntityType>>
    ): Promise<void>;
    getAggregateWithEntities(aggregateId: string): Promise<{
        aggregate: TAggregate | undefined;
        entities: {
            [K in keyof TEntityTypes]: TEntityTypes[K][];
        };
    }>
}

export interface IWriteToStore<T> {
    putAll: (values: T[]) => Promise<void>;
    put: (value: T) => Promise<void>;
    delete: (keys: string[] | string) => Promise<void>;
}

export interface IStoreWriter<
    TEventType extends number,
    TEvent extends BaseEvent<TEventType>,
    TAggregate extends BaseProjectMetadata,
    TEntityType extends BaseEntity<TEventType>,
    TEntityTypes extends Record<string, TEntityType>
> {
    events: IWriteToStore<TEvent>;
    aggregates: IWriteToStore<TAggregate>;
    entities: {
        [K in keyof TEntityTypes]: IWriteToStore<TEntityTypes[K]>;
    };
}

export class EventSourceStore<
    TEventType extends number,
    TEvent extends BaseEvent<TEventType>,
    TAggregate extends BaseProjectMetadata,
    TEntityType extends BaseEntity<TEventType>,
    TEntityTypes extends Record<string, TEntityType>
> implements IEventSourceStore<TEventType, TEvent, TAggregate, TEntityType, TEntityTypes> {
    private dbSchema: DBSchema;
    private config: EventSourceStoreConfig;
    private storeList: string[];
    private entityStores: string[];

    constructor(config: EventSourceStoreConfig) {
        this.config = config;
        this.dbSchema = this.createDBSchema(config);
        this.storeList = [
            config.storeNames.events,
            config.storeNames.aggregates,
            ...Object.values(config.storeNames.entities)
        ];
        this.entityStores = Object.values(config.storeNames.entities);
    }

    private createDBSchema(config: EventSourceStoreConfig): DBSchema {
        const schema: any = {};

        // Events store
        schema[config.storeNames.events] = {
            key: [FIELD_CX],
            value: {} as TEvent,
            indexes: {
                [STOREINDEX_PI_CX]: [FIELD_PI, FIELD_CX],
                [STOREINDEX_PI_BX_CX]: [FIELD_PI, FIELD_BX, FIELD_CX],
            }
        };

        // Aggregates store
        schema[config.storeNames.aggregates] = {
            key: FIELD_ID,
            value: {} as TAggregate
        };

        // Entity stores
        for (const [, storeName] of Object.entries(config.storeNames.entities)) {
            schema[storeName] = {
                key: [FIELD_PI, FIELD_ID],
                value: {},
                indexes: {
                    STOREINDEX_PI: FIELD_PI
                }
            };
        }

        return schema as DBSchema;
    }

    private async openConnection() {
        const config = this.config; // Capture the config in a local variable

        return await openDB(
            config.appName,
            config.dbVersion,
            {
                async upgrade(db, oldVersion, newVersion, transaction) {
                    // Ensure events store exists
                    if (!db.objectStoreNames.contains(config.storeNames.events)) {
                        db.createObjectStore(config.storeNames.events, { keyPath: FIELD_CX, autoIncrement: true });
                    }

                    // Ensure aggregates store exists
                    if (!db.objectStoreNames.contains(config.storeNames.aggregates)) {
                        db.createObjectStore(config.storeNames.aggregates, { keyPath: FIELD_ID });
                    }

                    // Create PI_LI index if it doesn't exist
                    const aggregateStore = transaction.objectStore(config.storeNames.aggregates);
                    if (!aggregateStore.indexNames.contains(STOREINDEX_MODIFIED_ON)) {
                        aggregateStore.createIndex(STOREINDEX_MODIFIED_ON, [FIELD_MODIFIED_ON]);
                    }

                    // Entity stores - ensure all entity stores exist, even if they're newly added
                    for (const [, storeName] of Object.entries(config.storeNames.entities)) {
                        // Create entity store if it doesn't exist
                        if (!db.objectStoreNames.contains(storeName)) {
                            db.createObjectStore(storeName, { keyPath: [FIELD_PI, FIELD_ID] });
                        }
                    }

                    // Ensure all required indexes are created/maintained regardless of version
                    // Events store indexes
                    const eventsStore = transaction.objectStore(config.storeNames.events);

                    // Create PI_LI index if it doesn't exist
                    if (!eventsStore.indexNames.contains(STOREINDEX_PI_CX)) {
                        eventsStore.createIndex(STOREINDEX_PI_CX, [FIELD_PI, FIELD_CX]);
                    }

                    // Create PI_SI_LI index if it doesn't exist
                    if (!eventsStore.indexNames.contains(STOREINDEX_PI_BX_CX)) {
                        eventsStore.createIndex(STOREINDEX_PI_BX_CX, [FIELD_PI, FIELD_BX, FIELD_CX]);
                    }

                    // Entity stores indexes
                    for (const [, storeName] of Object.entries(config.storeNames.entities)) {
                        const entityStore = transaction.objectStore(storeName);

                        // Create projectId index if it doesn't exist
                        if (!entityStore.indexNames.contains(STOREINDEX_PI)) {
                            entityStore.createIndex(STOREINDEX_PI, FIELD_PI);
                        }
                    }

                    // Run client-provided custom upgrade logic if available
                    // This is useful for destructive operations such as removing entity stores
                    if (config.onUpgrade) {
                        config.onUpgrade(db, oldVersion, newVersion, transaction);
                    }
                }
            }
        );
    }

    private async openTransaction<T extends 'readonly' | 'readwrite'>(storeNames: string[], mode: T) {
        const connection = await this.openConnection();
        return connection.transaction(storeNames, mode);
    }

    saveWorld = async (
        aggregate: TAggregate,
        events: TEvent[],
        entitiesToSave: Map<keyof TEntityTypes, Set<TEntityType>>
    ): Promise<void> => {
        await this.writeAll(async (writer) => {
            // Save the aggregate metadata
            await writer.aggregates.put(aggregate);

            // Save all events
            if (events.length > 0) {
                await writer.events.putAll(events);
            }

            // Save all entity collections
            for (const [entityType, entities] of entitiesToSave.entries()) {
                if (entities.size > 0) {
                    await writer.entities[entityType].putAll(
                        Array.from(entities) as TEntityTypes[keyof TEntityTypes][]
                    );
                }
            }
        });
    }

    writeAll = async (operations: (worker: IStoreWriter<TEventType, TEvent, TAggregate, TEntityType, TEntityTypes>) => Promise<void>) => {
        const tx = await this.openTransaction(this.storeList, 'readwrite');
        try {
            await operations(this.createWriter(tx));
            await tx.done;
        } catch (error) {
            tx.abort();
            throw error;
        }
    }

    /**
     * Gets an aggregate and all its related entities in a single transaction
     * @param aggregateId The ID of the aggregate to load
     * @returns An object containing the aggregate and all its entities
     */
    getAggregateWithEntities = async (aggregateId: string): Promise<{
        aggregate: TAggregate | undefined;
        entities: {
            [K in keyof TEntityTypes]: TEntityTypes[K][];
        };
    }> => {
        // Create a list of all stores needed for this operation
        const storeNames = [
            this.config.storeNames.aggregates,
            ...Object.values(this.config.storeNames.entities)
        ];

        // Open a single readonly transaction for all stores
        const tx = await this.openTransaction(storeNames, 'readonly');

        // Get the aggregate
        const aggregate = await tx.objectStore(this.config.storeNames.aggregates).get(aggregateId);

        if (!aggregate) {
            return {
                aggregate: undefined,
                entities: {} as any
            };
        }

        // Create an array of promises to get all entities
        const entityPromises: Promise<any>[] = [];
        const entityKeys: (keyof TEntityTypes)[] = [];

        for (const [entityKey, storeName] of Object.entries(this.config.storeNames.entities)) {
            const key = entityKey as keyof TEntityTypes;
            entityKeys.push(key);
            entityPromises.push(
                tx.objectStore(storeName).index(STOREINDEX_PI).getAll(aggregateId)
            );
        }

        // Wait for all entity queries to complete
        const results = await Promise.all(entityPromises);

        // Build the result object
        const entities = {} as any;
        for (let i = 0; i < entityKeys.length; i++) {
            entities[entityKeys[i]] = results[i];
        }

        return {
            aggregate,
            entities: entities as {
                [K in keyof TEntityTypes]: TEntityTypes[K][];
            }
        };
    }

    getAggregateById = async (aggregateId: string): Promise<TAggregate | undefined> => {
        const tx = await this.openTransaction([this.config.storeNames.aggregates], 'readonly');
        return await tx.objectStore(this.config.storeNames.aggregates).get(aggregateId);
    }

    getAllAggregates = async (): Promise<TAggregate[]> => {
        const tx = await this.openTransaction([this.config.storeNames.aggregates], 'readonly');
        const index = tx.objectStore(this.config.storeNames.aggregates).index(STOREINDEX_MODIFIED_ON);

        const aggregates: TAggregate[] = [];
        let cursor = await index.openCursor(null, 'prev');

        while (cursor) {
            aggregates.push(cursor.value);
            // eslint-disable-next-line no-await-in-loop
            cursor = await cursor.continue();
        }

        return aggregates;
    }

    isAggregateExist = async (aggregateId: string): Promise<boolean> => {
        const tx = await this.openTransaction([this.config.storeNames.aggregates], 'readonly');
        const store = tx.objectStore(this.config.storeNames.aggregates);
        const count = await store.count(aggregateId);
        return count > 0;
    }


    deleteAggregate = async (aggregateId: string): Promise<void> => {
        const tx = await this.openTransaction(this.storeList, 'readwrite');

        try {
            // Delete the aggregate
            await tx.objectStore(this.config.storeNames.aggregates).delete(aggregateId);

            // Delete all events for this aggregate
            const eventsStore = tx.objectStore(this.config.storeNames.events);
            const eventsIndex = eventsStore.index(STOREINDEX_PI_CX);

            // Use a cursor to delete all events for this aggregate
            let eventCursor = await eventsIndex.openCursor(IDBKeyRange.bound(
                [aggregateId, 0],
                [aggregateId, Infinity]
            ));

            while (eventCursor) {
                await eventCursor.delete();
                // eslint-disable-next-line no-await-in-loop
                eventCursor = await eventCursor.continue();
            }

            // Delete all entities for this aggregate
            for (const storeName of this.entityStores) {
                const entityStore = tx.objectStore(storeName);
                const entityIndex = entityStore.index(STOREINDEX_PI);

                // Use a cursor to delete all entities for this aggregate
                let entityCursor = await entityIndex.openCursor(aggregateId);

                while (entityCursor) {
                    await entityCursor.delete();
                    // eslint-disable-next-line no-await-in-loop
                    entityCursor = await entityCursor.continue();
                }
            }

            await tx.done;
        } catch (error) {
            tx.abort();
            throw error;
        }
    }

    getMostRecentAggregate = async (): Promise<TAggregate | undefined> => {
        const tx = await this.openTransaction([this.config.storeNames.aggregates], 'readonly');
        const index = tx.objectStore(this.config.storeNames.aggregates).index(STOREINDEX_MODIFIED_ON);

        const cursor = await index.openCursor(null, 'prev');
        return cursor?.value;
    }

    getEvents = async <T extends TEvent>(aggregateId: string, fromClientIndex: number = 0, toClientIndex: number = Infinity): Promise<T[]> => {
        const tx = await this.openTransaction([this.config.storeNames.events], 'readonly');
        const index = tx.objectStore(this.config.storeNames.events).index(STOREINDEX_PI_CX);

        const range = IDBKeyRange.bound(
            [aggregateId, fromClientIndex],
            [aggregateId, toClientIndex]
        );

        return await index.getAll(range) as T[];
    }

    getServerEvents = async <T extends TEvent>(aggregateId: string, fromBatchIndex: number = 0, toBatchIndex: number = Infinity, fromClientIndex: number = 0, toClientIndex: number = Infinity): Promise<T[]> => {
        const tx = await this.openTransaction([this.config.storeNames.events], 'readonly');
        const index = tx.objectStore(this.config.storeNames.events).index(STOREINDEX_PI_BX_CX);

        const range = IDBKeyRange.bound(
            [aggregateId, fromBatchIndex, fromClientIndex],
            [aggregateId, toBatchIndex, toClientIndex]
        );

        return await index.getAll(range) as T[];
    }

    getEntitiesByAggregateId = async <K extends keyof TEntityTypes>(
        entityType: K,
        aggregateId: string
    ): Promise<TEntityTypes[K][]> => {
        const storeName = this.config.storeNames.entities[entityType as string];
        const tx = await this.openTransaction([storeName], 'readonly');
        const index = tx.objectStore(storeName).index(STOREINDEX_PI);
        return await index.getAll(aggregateId);
    }

    replaceWorld = async (
        newMetadata: TAggregate,
        oldEntities: Record<string, EntityCollection<TEntityType>>,
        newEntities: Record<string, EntityCollection<TEntityType>>
    ): Promise<void> => {
        await this.writeAll(async (writer) => {
            await writer.aggregates.put(newMetadata);

            // Iterate through entity store configuration
            // Try to find matching collections in the worlds
            for (const [storeKey, _] of Object.entries(this.config.storeNames.entities)) {
                const typedStoreKey = storeKey as keyof TEntityTypes;

                // Get the old collection if it exists
                const oldCollection = oldEntities[storeKey];
                const newCollection = newEntities[storeKey];

                // Only process deletions if both collections exist
                if (oldCollection instanceof EntityCollection &&
                    newCollection instanceof EntityCollection) {
                    // Delete entities that no longer exist in the new world
                    oldCollection.all
                        .filter(x => !newCollection.isExistIgnoreDeleted(x.id))
                        .forEach(x => {
                            writer.entities[typedStoreKey].delete(
                                [newMetadata.id, x.id]
                            );
                        });
                }

                if (newCollection instanceof EntityCollection) {
                    // Add or update all current entities
                    await writer.entities[storeKey as keyof TEntityTypes].putAll(
                        newCollection.all as any
                    );
                }
            }
        });
    }

    private createWriter(tx: IDBPTransaction<any, string[], "readwrite">): IStoreWriter<TEventType, TEvent, TAggregate, TEntityType, TEntityTypes> {
        const writer: any = {
            events: new WriteToStore<TEvent>(tx.objectStore(this.config.storeNames.events)),
            aggregates: new WriteToStore<TAggregate>(tx.objectStore(this.config.storeNames.aggregates)),
            entities: {}
        };

        for (const [entityKey, storeName] of Object.entries(this.config.storeNames.entities)) {
            writer.entities[entityKey] = new WriteToStore(tx.objectStore(storeName));
        }

        return writer as IStoreWriter<TEventType, TEvent, TAggregate, TEntityType, TEntityTypes>;
    }
}

export class WriteToStore<T> implements IWriteToStore<T> {
    constructor(private store: IDBPObjectStore<any, any[], any, any>) { }

    putAll = async (values: T[]): Promise<void> => {
        await Promise.all(values.map(value => this.store.put!(value)));
    }

    put = async (value: T): Promise<void> => {
        await this.store.put!(value);
    }

    delete = async (keys: string[] | string): Promise<void> => {
        await this.store.delete!(keys);
    }
}